import {
  BulkAction,
  BulkModalBodyProps,
} from '@app/components/data-table/bulk-edit/types';
import { useBulkUpdatePurchasedGoods } from '@shared/api/hooks/purchased-goods/bulk';
import { useBulkUpdateSoldGoods } from '@shared/api/hooks/sold-goods/bulk';
import { OperationReportTypeEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/components/ui/use-toast';
import { useOptimisticFoodResolution } from './use-optimistic-food-resolution';

function BulkConfirmModalBody({
  selectedUuids,
  selectedCount,
  onClose,
  onCompleted,
  reportType = OperationReportTypeEnum.purchase,
}: BulkModalBodyProps & { reportType?: OperationReportTypeEnum }) {
  const { toast } = useToast();
  // Both hooks are instantiated unconditionally (rules of hooks); only the one
  // matching reportType is used.
  const purchasedBulk = useBulkUpdatePurchasedGoods();
  const soldBulk = useBulkUpdateSoldGoods();
  const { mutateAsync, isLoading } =
    reportType === OperationReportTypeEnum.sales ? soldBulk : purchasedBulk;
  const resolveRows = useOptimisticFoodResolution(reportType);

  const itemsLabel = `${selectedCount} item${selectedCount === 1 ? '' : 's'}`;

  const handleConfirm = async () => {
    try {
      await mutateAsync({
        data: { action: 'confirm', uuids: selectedUuids },
      });
      resolveRows(selectedUuids);
      toast({
        title: 'Items confirmed',
        description: `Confirmed ${itemsLabel}.`,
      });
      onCompleted();
    } catch (error) {
      toast({
        title: "Couldn't confirm items",
        description:
          'There was an error confirming the selected items. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-5 pt-2">
      <Text variant="subtle" className="text-gray-600">
        Confirms these items as-is. You can still edit them later in Food &
        drink.
      </Text>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          loading={isLoading}
          onClick={handleConfirm}
        >
          Confirm {itemsLabel}
        </Button>
      </div>
    </div>
  );
}

export const makeBulkConfirmAction = ({
  reportType,
}: {
  /** Purchased vs sold endpoints; defaults to purchase. */
  reportType?: OperationReportTypeEnum;
} = {}): BulkAction => ({
  kind: 'confirm',
  label: 'Confirm',
  icon: 'check',
  variant: 'outline',
  ModalBody: (props) => (
    <BulkConfirmModalBody {...props} reportType={reportType} />
  ),
});
